import { type useTranslations } from "next-intl";

import { type RouterInputs, type RouterOutputs } from "~/trpc/types";

import { type TourCartLine } from "./use-cart";

export type ProductId = RouterInputs["tours"]["choices"]["productId"];
export type TourConfig = RouterOutputs["tours"]["config"];
export type PassengerCategory = TourConfig["passengerCategories"][0] & {
  count: number;
};
export type PassengerCategoryFieldValue = Pick<
  PassengerCategory,
  "id" | "count"
>;

export function validate({
  t,
  totalQuantityAvailable,
  totalPassengers,
  maxPassengersPerBooking,
  minPassengersPerBooking,
}: {
  t: ReturnType<typeof useTranslations>;
  totalQuantityAvailable?: number;
  totalPassengers: number;
  maxPassengersPerBooking?: number;
  minPassengersPerBooking?: number;
}) {
  const errors: string[] = [];

  if (
    typeof totalQuantityAvailable === "number" &&
    totalPassengers > totalQuantityAvailable
  ) {
    errors.push(
      t("noTicketsAvailableError", {
        availableQuantity: totalQuantityAvailable,
      }),
    );
  }

  if (
    typeof maxPassengersPerBooking === "number" &&
    totalPassengers > maxPassengersPerBooking
  ) {
    errors.push(
      t("maximumOccupancyError", {
        maxPassengersPerBooking,
      }),
    );
  }
  if (
    typeof minPassengersPerBooking === "number" &&
    totalPassengers < minPassengersPerBooking
  ) {
    errors.push(
      t("minimumOccupancyError", {
        minPassengersPerBooking,
      }),
    );
  }
  return errors;
}

function getPassengerCategoryIdFromLineItem(
  lineItemPassenger: TourCartLine["lineItem"]["passengers"][0],
) {
  if ("linktivityId" in lineItemPassenger) {
    return lineItemPassenger.linktivityId;
  }
  return lineItemPassenger.categoryId ?? "";
}

export function getPassengerCategories(
  state: PassengerCategoryFieldValue[],
  cartLine?: TourCartLine,
  config?: TourConfig,
): PassengerCategory[] {
  if (!config) {
    return [];
  }
  return config.passengerCategories.map((passengerCategory) => {
    return {
      ...passengerCategory,
      count:
        state.find(({ id }) => id === passengerCategory.id)?.count ??
        cartLine?.lineItem.passengers.find(
          (passenger) =>
            getPassengerCategoryIdFromLineItem(passenger) ===
            passengerCategory.id,
        )?.count ??
        0,
    };
  });
}

export function getProductId({
  bokunExperienceId,
  linktivityActivityId,
}: {
  bokunExperienceId?: string | null;
  linktivityActivityId?: string | null;
}): ProductId | null {
  if (bokunExperienceId) {
    return { type: "bokun" as const, id: bokunExperienceId };
  }
  if (linktivityActivityId) {
    return { type: "linktivity" as const, id: linktivityActivityId };
  }

  return null;
}
